import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';

import hisselerVeri from '../data/bist-hisseler.json';
import { sonFiyatGetir, birdenFazlaFiyatGetir } from '../services/isyatirimApi';
import { islemleriGetir, islemEkle, pozisyonlariGetir } from '../services/islemlerApi';
import { hesabimGetir } from '../services/hesabimApi';
import { fiyatFormatla, isaretliFormatla, degisimHesapla } from '../utils/fiyat';

const TUM_HISSELER = hisselerVeri.data;

function HisseAramaKutusu({ hisseler, sorgu, setSorgu, onSec, placeholder }) {
  const aramaTerimi = sorgu.trim().toUpperCase();
  const sonuclar = aramaTerimi
    ? hisseler.filter(h => h.kod.includes(aramaTerimi) || h.ad.toUpperCase().includes(aramaTerimi)).slice(0, 15)
    : [];

  return (
    <div>
      <input
        type="text" value={sorgu} onChange={e => setSorgu(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400/40 focus:border-sky-400 transition-all"
      />

      {sonuclar.length > 0 && (
        <ul className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden max-h-56 overflow-y-auto">
          {sonuclar.map(h => (
            <li key={h.kod} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50">
              <span className="text-sm">
                <span className="font-semibold text-slate-800">{h.kod}</span>
                <span className="text-slate-500"> — {h.ad}</span>
              </span>
              <button
                type="button" onClick={() => onSec(h.kod)}
                className="text-xs font-semibold text-sky-600 hover:text-sky-700 shrink-0 ml-3"
              >Seç</button>
            </li>
          ))}
        </ul>
      )}

      {aramaTerimi && sonuclar.length === 0 && (
        <p className="mt-2 text-sm text-slate-500">Sonuç bulunamadı.</p>
      )}
    </div>
  );
}

// Alım ve satım panelleri; fiyat her zaman isyatirim.com.tr'den anlık
// çekilir ve değiştirilemez (elle giriş yok).
function IslemPaneli({ tip, hisseler, baslik, aciklama, bosMesaj, dugmeRenk, onIslemEklendi, disaridanSecim }) {
  const [sorgu, setSorgu] = useState('');
  const [hisseKodu, setHisseKodu] = useState('');
  const [adet, setAdet] = useState('');
  const [fiyat, setFiyat] = useState(null);
  const [fiyatYukleniyor, setFiyatYukleniyor] = useState(false);
  const [fiyatHatasi, setFiyatHatasi] = useState('');
  const [hata, setHata] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [yenidenDeneSayaci, setYenidenDeneSayaci] = useState(0);
  const sonIslenenSecimId = useRef(null);

  function hisseSec(kod) {
    setHisseKodu(kod);
    setSorgu('');
    setFiyat(null);
    setFiyatHatasi('');
    setFiyatYukleniyor(true);
  }

  function hisseyiDegistir() {
    setHisseKodu('');
    setFiyat(null);
    setFiyatHatasi('');
  }

  useEffect(() => {
    if (disaridanSecim && disaridanSecim.id !== sonIslenenSecimId.current) {
      sonIslenenSecimId.current = disaridanSecim.id;
      hisseSec(disaridanSecim.kod);
    }
  }, [disaridanSecim]);

  // Hisse seçildiğinde (ve "Tekrar dene"/"Fiyatı yenile" ile) fiyat tek
  // seferlik istekle çekilir; panel açık kaldığı sürece sürekli bir
  // bağlantı tutulmaz, fiyat kendiliğinden güncellenmez.
  useEffect(() => {
    if (!hisseKodu) return;

    let iptalEdildi = false;
    setFiyat(null);
    setFiyatHatasi('');
    setFiyatYukleniyor(true);

    sonFiyatGetir(hisseKodu)
      .then(veri => {
        if (iptalEdildi) return;
        setFiyat(veri.last);
        setFiyatYukleniyor(false);
      })
      .catch(() => {
        if (iptalEdildi) return;
        setFiyatYukleniyor(false);
        setFiyatHatasi('Anlık fiyat alınamadı');
      });

    return () => { iptalEdildi = true; };
  }, [hisseKodu, yenidenDeneSayaci]);

  function gonder(event) {
    event.preventDefault();
    setHata('');
    setGonderiliyor(true);

    islemEkle(hisseKodu, tip, Number(adet), fiyat)
      .then(yeniIslem => {
        onIslemEklendi({ ...yeniIslem, created_at: new Date().toISOString() });
        setHisseKodu('');
        setAdet('');
        setFiyat(null);
      })
      .catch(err => setHata(err.message))
      .finally(() => setGonderiliyor(false));
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h1 className="text-lg font-bold text-slate-800 mb-1">{baslik}</h1>
      <p className="text-sm text-slate-500 mb-4">{aciklama}</p>

      {hisseler.length === 0 ? (
        <p className="text-sm text-slate-500">{bosMesaj}</p>
      ) : !hisseKodu ? (
        <HisseAramaKutusu
          hisseler={hisseler} sorgu={sorgu} setSorgu={setSorgu}
          onSec={hisseSec} placeholder="Hisse kodu veya adı ara (ör. THYAO, EREGLI...)"
        />
      ) : (
        <form onSubmit={gonder} className="flex flex-col gap-3">
          <div className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg bg-slate-50">
            <span className="text-sm font-semibold text-slate-800">{hisseKodu}</span>
            <button
              type="button" onClick={hisseyiDegistir}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700"
            >Değiştir</button>
          </div>

          <div className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg bg-slate-50">
            <span className="text-xs text-slate-500">Anlık Fiyat</span>
            {fiyat !== null ? (
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">{fiyatFormatla(fiyat)}</span>
                <button
                  type="button" onClick={() => setYenidenDeneSayaci(onceki => onceki + 1)}
                  className="text-xs font-semibold text-sky-600 hover:text-sky-700"
                >Yenile</button>
              </span>
            ) : fiyatHatasi ? (
              <span className="flex items-center gap-2">
                <span className="text-xs text-red-600">{fiyatHatasi}</span>
                <button
                  type="button" onClick={() => setYenidenDeneSayaci(onceki => onceki + 1)}
                  className="text-xs font-semibold text-sky-600 hover:text-sky-700"
                >Tekrar dene</button>
              </span>
            ) : (
              <span className="text-sm text-slate-400">Alınıyor...</span>
            )}
          </div>

          <input
            type="number" required min="1" step="1" placeholder="Adet"
            value={adet} onChange={e => setAdet(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400/40 focus:border-sky-400 transition-all"
          />

          <div className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg bg-slate-50">
            <span className="text-xs text-slate-500">Toplam</span>
            <span className="text-sm font-semibold text-slate-800">
              {fiyat !== null && adet ? fiyatFormatla(fiyat * Number(adet)) : '—'}
            </span>
          </div>

          <button
            type="submit" disabled={gonderiliyor || fiyatYukleniyor || fiyat === null}
            className={`w-full px-4 py-2 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-semibold rounded-full shadow-sm transition-colors ${dugmeRenk}`}
          >
            {gonderiliyor ? 'Kaydediliyor...' : (tip === 'alim' ? 'Alım Ekle' : 'Satım Ekle')}
          </button>

          {hata && <p className="text-sm text-red-600">{hata}</p>}
        </form>
      )}
    </div>
  );
}

function AlimSatim() {
  const [islemler, setIslemler] = useState([]);
  const [listeYukleniyor, setListeYukleniyor] = useState(true);
  const [listeHatasi, setListeHatasi] = useState('');

  const [pozisyonlar, setPozisyonlar] = useState([]);
  const [pozisyonHatasi, setPozisyonHatasi] = useState('');
  const [anlikFiyatlar, setAnlikFiyatlar] = useState({});

  const [bakiye, setBakiye] = useState(0);
  const [bakiyeYukleniyor, setBakiyeYukleniyor] = useState(true);

  const [alimSecimi, setAlimSecimi] = useState(null);
  const [satimSecimi, setSatimSecimi] = useState(null);

  // Bir işlem satırına tıklandığında altında kar/zarar, işlem no ve tam
  // tarih/saat gibi listede gösterilmeyen ayrıntıları açar.
  const [acikIslemler, setAcikIslemler] = useState(() => new Set());

  function islemAcKapa(id) {
    setAcikIslemler(onceki => {
      const yeni = new Set(onceki);
      if (yeni.has(id)) {
        yeni.delete(id);
      } else {
        yeni.add(id);
      }
      return yeni;
    });
  }

  useEffect(() => {
    let iptalEdildi = false;

    islemleriGetir()
      .then(veri => {
        if (iptalEdildi) return;
        setIslemler(veri);
        setListeYukleniyor(false);
      })
      .catch(err => {
        if (iptalEdildi) return;
        setListeHatasi(err.message);
        setListeYukleniyor(false);
      });

    pozisyonlariGetir()
      .then(veri => {
        if (iptalEdildi) return;
        setPozisyonlar(veri);
      })
      .catch(err => {
        if (iptalEdildi) return;
        setPozisyonHatasi(err.message);
      });

    hesabimGetir()
      .then(veri => {
        if (iptalEdildi) return;
        setBakiye(veri.bakiye);
        setBakiyeYukleniyor(false);
      })
      .catch(() => {
        if (iptalEdildi) return;
        setBakiyeYukleniyor(false);
      });

    return () => { iptalEdildi = true; };
  }, []);

  function pozisyonlariYenile() {
    pozisyonlariGetir()
      .then(veri => setPozisyonlar(veri))
      .catch(err => setPozisyonHatasi(err.message));
  }

  // Portföydeki hisse kodları listesi.
  const pozisyonKodlari = useMemo(
    () => [...new Set(pozisyonlar.map(p => p.hisse_kodu))].sort(),
    [pozisyonlar]
  );
  const pozisyonKodlariAnahtari = pozisyonKodlari.join(',');

  // Fiyatlar sürekli açık bir bağlantıyla değil, istendiğinde (portföydeki
  // hisseler değiştiğinde ve navbar'daki "Yenile" düğmesine basıldığında)
  // tek seferlik istekle çekiliyor.
  async function pozisyonFiyatlariniYenile(kodlar) {
    if (kodlar.length === 0) return;
    const sonuclar = await birdenFazlaFiyatGetir(kodlar);
    setAnlikFiyatlar(onceki => ({ ...onceki, ...sonuclar }));
  }

  useEffect(() => {
    pozisyonFiyatlariniYenile(pozisyonKodlari);
  }, [pozisyonKodlariAnahtari]);

  // Navbar'daki "Yenile" düğmesi bu event'i tetikler; bakiye, işlemler,
  // pozisyonlar ve anlık fiyatların hepsini yeniden çeker. Başka bir
  // sekmede yapılan değişiklikler (para yatırma, alım/satım) artık ancak
  // bu şekilde yansır.
  useEffect(() => {
    function hepsiniYenile() {
      hesabimGetir()
        .then(veri => {
          setBakiye(veri.bakiye);
          setBakiyeYukleniyor(false);
        })
        .catch(() => {});

      islemleriGetir()
        .then(veri => setIslemler(veri))
        .catch(err => setListeHatasi(err.message));

      pozisyonlariGetir()
        .then(veri => {
          setPozisyonlar(veri);
          const kodlar = [...new Set(veri.map(p => p.hisse_kodu))];
          pozisyonFiyatlariniYenile(kodlar);
        })
        .catch(err => setPozisyonHatasi(err.message));
    }

    window.addEventListener('veri-yenile', hepsiniYenile);
    return () => window.removeEventListener('veri-yenile', hepsiniYenile);
  }, []);

  function islemEklendi(yeniIslem) {
    setIslemler(onceki => [yeniIslem, ...onceki]);
    if (yeniIslem.bakiye !== undefined && yeniIslem.bakiye !== null) {
      setBakiye(Number(yeniIslem.bakiye));
    }
    pozisyonlariYenile();
  }

  // Satım yapılabilecek hisseler, kullanıcının elinde halen bulunan
  // (adet > 0) pozisyonlarla sınırlı.
  const satilabilirHisseler = useMemo(
    () => TUM_HISSELER.filter(h => pozisyonlar.some(p => p.hisse_kodu === h.kod)),
    [pozisyonlar]
  );

  // Portföyüm tablosunun alt toplamları; yalnızca anlık fiyatı gelmiş
  // pozisyonlar hesaba katılıyor (henüz gelmeyenler geçici olarak dışarıda
  // bırakılıyor, satır bazında "—" ile aynı mantık).
  const portfoyToplamlari = useMemo(() => {
    let toplamTutar = 0;
    let toplamKarZarar = 0;
    let toplamMaliyet = 0;

    pozisyonlar.forEach(p => {
      const anlikFiyat = anlikFiyatlar[p.hisse_kodu]?.last;
      if (anlikFiyat == null) return;
      const ortalamaMaliyet = Number(p.ortalama_maliyet);
      toplamTutar += p.adet * anlikFiyat;
      toplamKarZarar += (anlikFiyat - ortalamaMaliyet) * p.adet;
      toplamMaliyet += p.adet * ortalamaMaliyet;
    });

    const yuzde = toplamMaliyet > 0 ? (toplamKarZarar / toplamMaliyet) * 100 : 0;
    return { toplamTutar, toplamKarZarar, yuzde };
  }, [pozisyonlar, anlikFiyatlar]);

  // İşlemlerim listesi zaten en yeniden en eskiye sıralı geldiğinden, aynı
  // güne ait işlemler ardışık gelir; bunları tek bir tarih ara başlığı
  // altında grupluyoruz.
  const islemGruplari = useMemo(() => {
    const gruplar = [];
    let sonAnahtar = null;

    islemler.forEach(islem => {
      const tarih = new Date(islem.created_at);
      const anahtar = tarih.toDateString();

      if (anahtar !== sonAnahtar) {
        sonAnahtar = anahtar;
        gruplar.push({
          etiket: tarih.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }),
          islemler: []
        });
      }

      gruplar[gruplar.length - 1].islemler.push(islem);
    });

    return gruplar;
  }, [islemler]);

  function portfoydenAl(kod) {
    setAlimSecimi({ kod, id: Date.now() });
  }

  function portfoydenSat(kod) {
    setSatimSecimi({ kod, id: Date.now() });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl bg-slate-900 px-6 py-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Nakit Bakiye</p>
        <p className="mt-1 text-3xl font-black text-white">
          {bakiyeYukleniyor ? '—' : fiyatFormatla(bakiye) + ' ₺'}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <IslemPaneli
          tip="alim" hisseler={TUM_HISSELER} baslik="Alım Yap"
          aciklama="Hisseyi arayıp seçin, fiyat isyatirim.com.tr'den anlık alınır."
          dugmeRenk="bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
          onIslemEklendi={islemEklendi}
          disaridanSecim={alimSecimi}
        />
        <IslemPaneli
          tip="satim" hisseler={satilabilirHisseler} baslik="Satım Yap"
          aciklama="Yalnızca daha önce alım yaptığınız hisseleri satabilirsiniz; fiyat anlık alınır."
          bosMesaj="Satış yapabilmek için önce bir hisse alımı kaydetmelisiniz."
          dugmeRenk="bg-red-100 hover:bg-red-200 text-red-700"
          onIslemEklendi={islemEklendi}
          disaridanSecim={satimSecimi}
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="text-lg font-bold text-slate-800 mb-4">Portföyüm</div>

        {pozisyonHatasi && <p className="text-sm text-red-600 mb-3">{pozisyonHatasi}</p>}

        {pozisyonlar.length === 0 ? (
          <p className="text-sm text-slate-500">Elinizde şu anda hisse bulunmuyor.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Hisse</th>
                  <th className="py-2 pr-3">Adet</th>
                  <th className="py-2 pr-3">Maliyet</th>
                  <th className="py-2 pr-3">Anlık Fiyat</th>
                  <th className="py-2 pr-3">Günlük Değişim</th>
                  <th className="py-2 pr-3">Toplam Tutar</th>
                  <th className="py-2 pr-3">Kar/Zarar %</th>
                  <th className="py-2 pr-3">Kar/Zarar</th>
                  <th className="py-2 pr-3">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {pozisyonlar.map(p => {
                  const anlikVeri = anlikFiyatlar[p.hisse_kodu];
                  const anlikFiyat = anlikVeri != null ? anlikVeri.last : null;
                  const gunlukDegisim = anlikVeri != null ? degisimHesapla(anlikVeri.last, anlikVeri.dayClose) : null;
                  const ortalamaMaliyet = Number(p.ortalama_maliyet);
                  const karZarar = anlikFiyat != null ? (anlikFiyat - ortalamaMaliyet) * p.adet : null;
                  const karZararYuzde = anlikFiyat != null && ortalamaMaliyet > 0
                    ? ((anlikFiyat - ortalamaMaliyet) / ortalamaMaliyet) * 100
                    : null;
                  return (
                    <tr key={p.hisse_kodu} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-semibold">
                        <NavLink to={`/hisse/${p.hisse_kodu.toLowerCase()}`} className="text-slate-800 hover:text-sky-600">
                          {p.hisse_kodu}
                        </NavLink>
                      </td>
                      <td className="py-2 pr-3 text-slate-600">{p.adet}</td>
                      <td className="py-2 pr-3 text-slate-600">{fiyatFormatla(ortalamaMaliyet)}</td>
                      <td className="py-2 pr-3 text-slate-600">{anlikFiyat != null ? fiyatFormatla(anlikFiyat) : '—'}</td>
                      <td className="py-2 pr-3 font-semibold">
                        {gunlukDegisim === null ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <span className={gunlukDegisim.fark >= 0 ? 'text-emerald-700' : 'text-red-600'}>
                            {isaretliFormatla(gunlukDegisim.yuzde, '%')}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 font-semibold text-slate-800">{anlikFiyat != null ? fiyatFormatla(p.adet * anlikFiyat) : '—'}</td>
                      <td className="py-2 pr-3 font-semibold">
                        {karZararYuzde === null ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <span className={karZararYuzde >= 0 ? 'text-emerald-700' : 'text-red-600'}>
                            {isaretliFormatla(karZararYuzde, '%')}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 font-semibold">
                        {karZarar === null ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <span className={karZarar >= 0 ? 'text-emerald-700' : 'text-red-600'}>
                            {karZarar >= 0 ? '+' : ''}{fiyatFormatla(karZarar)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex gap-2">
                          <button
                            type="button" onClick={() => portfoydenAl(p.hisse_kodu)}
                            className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold hover:bg-emerald-200 transition-colors"
                          >Al</button>
                          <button
                            type="button" onClick={() => portfoydenSat(p.hisse_kodu)}
                            className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold hover:bg-red-200 transition-colors"
                          >Sat</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200">
                  <td colSpan={5} className="py-2 pr-3 font-semibold text-slate-800">Toplam</td>
                  <td className="py-2 pr-3 font-semibold text-slate-800">{fiyatFormatla(portfoyToplamlari.toplamTutar)}</td>
                  <td className="py-2 pr-3 font-semibold">
                    <span className={portfoyToplamlari.toplamKarZarar >= 0 ? 'text-emerald-700' : 'text-red-600'}>
                      {isaretliFormatla(portfoyToplamlari.yuzde, '%')}
                    </span>
                  </td>
                  <td className="py-2 pr-3 font-semibold">
                    <span className={portfoyToplamlari.toplamKarZarar >= 0 ? 'text-emerald-700' : 'text-red-600'}>
                      {portfoyToplamlari.toplamKarZarar >= 0 ? '+' : ''}{fiyatFormatla(portfoyToplamlari.toplamKarZarar)}
                    </span>
                  </td>
                  <td className="py-2 pr-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="text-lg font-bold text-slate-800 mb-4">İşlemlerim</div>

        {listeHatasi && <p className="text-sm text-red-600 mb-3">{listeHatasi}</p>}
        {listeYukleniyor && <p className="text-sm text-slate-500">Yükleniyor...</p>}

        {!listeYukleniyor && islemler.length === 0 && (
          <p className="text-sm text-slate-500">Henüz kaydedilmiş bir işlem yok.</p>
        )}

        {!listeYukleniyor && islemler.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Saat</th>
                  <th className="py-2 pr-3">Hisse</th>
                  <th className="py-2 pr-3">Tip</th>
                  <th className="py-2 pr-3">Adet</th>
                  <th className="py-2 pr-3">Fiyat</th>
                  <th className="py-2 pr-3">Tutar</th>
                </tr>
              </thead>
              {islemGruplari.map(grup => {
                return (
                  <tbody key={grup.etiket}>
                    <tr>
                      <td colSpan={6} className="pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {grup.etiket}
                      </td>
                    </tr>
                    {grup.islemler.map(islem => {
                      const islemAcik = acikIslemler.has(islem.id);
                      const karZarar = islem.kar_zarar !== null && islem.kar_zarar !== undefined ? Number(islem.kar_zarar) : null;

                      return (
                        <React.Fragment key={islem.id}>
                          <tr
                            onClick={() => islemAcKapa(islem.id)}
                            className="border-b border-slate-100 cursor-pointer hover:bg-slate-50"
                          >
                            <td className="py-2 pr-3 text-slate-500 text-xs">{new Date(islem.created_at).toLocaleTimeString('tr-TR')}</td>
                            <td className="py-2 pr-3 font-semibold text-slate-800">{islem.hisse_kodu}</td>
                            <td className="py-2 pr-3">
                              <span className={
                                'inline-block px-2 py-0.5 rounded-full text-xs font-semibold ' +
                                (islem.islem_tipi === 'alim' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700')
                              }>
                                {islem.islem_tipi === 'alim' ? 'Alım' : 'Satım'}
                              </span>
                            </td>
                            <td className="py-2 pr-3 text-slate-600">{islem.adet}</td>
                            <td className="py-2 pr-3 text-slate-600">{fiyatFormatla(Number(islem.fiyat))}</td>
                            <td className="py-2 pr-3 font-semibold text-slate-800">{fiyatFormatla(islem.adet * Number(islem.fiyat))}</td>
                          </tr>
                          {islemAcik && (
                            <tr className="border-b border-slate-100 bg-slate-50">
                              <td colSpan={6} className="py-3 px-3">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-xs">
                                  <div>
                                    <p className="text-slate-400">İşlem No</p>
                                    <p className="font-semibold text-slate-800">#{islem.id}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-400">Tarih</p>
                                    <p className="font-semibold text-slate-800">
                                      {new Date(islem.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                                      {' '}{new Date(islem.created_at).toLocaleTimeString('tr-TR')}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-slate-400">Fiyat</p>
                                    <p className="font-semibold text-slate-800">{fiyatFormatla(Number(islem.fiyat))}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-400">Kar/Zarar</p>
                                    <p className={
                                      'font-semibold ' +
                                      (karZarar === null ? 'text-slate-400' : karZarar >= 0 ? 'text-emerald-600' : 'text-red-600')
                                    }>
                                      {karZarar !== null ? isaretliFormatla(karZarar) : '—'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                );
              })}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AlimSatim
